import WorldMap from '../WorldMap.js';
import WorldNPC from '../WorldNPC.js';
import WorldPlayer from '../WorldPlayer.js';
import WorldUI from '../WorldUI.js';
import WorldInteraction from '../WorldInteraction.js';
import { GAME_CONSTANTS } from '../GameConstants.js';
import SettingsHelper from '../SettingsHelper.js';
import DebugHelper from '../DebugHelper.js';
import MessageHelper from '../MessageHelper.js';
import VehicleEquipmentSystem from '../VehicleEquipmentSystem.js';
import GameStateStore from '../GameStateStore.js';
import { ensureAudios } from '../LazyAssetLoader.js';
import { resolveGlobalSpawn } from '../WorldChunkCoords.js';
import { isCave, isTown } from '../SceneClassHelper.js';

export default class BaseWorldInitializer {
    constructor(scene) {
        this.scene = scene;
    }
    
    /**
     * 初始化场景数据
     * @param {Object} data - 初始化数据
     * @returns {Object} 初始化结果 {player, isMultiplayer, networkManager}
     */
    init(data = {}) {
        this.scene.gameState = GameStateStore.getState();
        this.scene.worldMapJson = this.scene.cache.json.get(this.scene.mapKey);
        this.scene.isChunkedWorld = this.scene.mapKey === 'world_meta';
        
        if (!this.scene.worldMapJson) {
            DebugHelper.error(`BaseWorldScene: 无法加载地图数据 ${this.scene.mapKey}`);
            return null;
        }
        
        // 保存当前场景信息
        if (this.scene.gameState) {
            GameStateStore.setCurrentScene(this.scene.scene.key);
            GameStateStore.patchPlayer({ currentScene: this.scene.scene.key });
        }
        
        // 检查是否是多人游戏模式
        const isMultiplayer = data.multiplayer || GameStateStore.isMultiplayerConnected();
        const networkManager = data.networkManager || window.networkManager || null;
        
        // 多人游戏模式下，如果传入了playerName，更新gameState
        if (isMultiplayer && data.playerName && GameStateStore.getPlayer()) {
            GameStateStore.patchPlayer({ name: data.playerName });
        }
        
        // 保存多人游戏模式标志，供createPlayerData使用
        this.scene.isMultiplayer = isMultiplayer;
        
        // 获取玩家位置
        const playerPosition = this.getPlayerPosition(data);
        const player = this.createPlayerData(playerPosition);
        const inVehicle = player ? player.inVehicle : false;
        
        // 保存过渡标记
        const needsTransitionIn = data.transitionIn || false;
        
        return {
            player,
            inVehicle,
            isMultiplayer,
            networkManager,
            needsTransitionIn
        };
    }
    
    /**
     * 获取玩家位置
     * @param {Object} data - 初始化数据
     * @returns {Object} {x, y} 玩家位置
     */
    getPlayerPosition(data) {
        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        
        // 优先使用传入的spawn坐标
        if (data.spawnX !== undefined && data.spawnY !== undefined) {
            return {
                x: data.spawnX * tileSize + tileSize / 2,
                y: data.spawnY * tileSize + tileSize / 2
            };
        }
        
        // 否则使用游戏状态中的坐标
        if (this.scene.gameState && this.scene.gameState.player) {
            return {
                x: this.scene.gameState.player.tileX * tileSize + tileSize / 2,
                y: this.scene.gameState.player.tileY * tileSize + tileSize / 2
            };
        }
        
        // 如果地图配置中有defaultSpawn，使用它
        if (this.scene.worldMapJson && this.scene.worldMapJson.defaultSpawn) {
            const spawn = this.scene.worldMapJson.defaultSpawn;

            if (this.scene.isChunkedWorld) {
                const { globalTileX, globalTileY } = resolveGlobalSpawn(spawn, this.scene.worldMapJson);
                return {
                    x: globalTileX * tileSize + tileSize / 2,
                    y: globalTileY * tileSize + tileSize / 2
                };
            }

            return {
                x: spawn.tileX * tileSize + tileSize / 2,
                y: spawn.tileY * tileSize + tileSize / 2
            };
        }
        
        // 默认位置
        return { x: 400, y: 400 };
    }

    /**
     * 场景 wake 时应用传送点坐标
     * @param {Object} data
     */
    applySpawnPosition(data) {
        if (data?.spawnX === undefined || data?.spawnY === undefined) {
            return;
        }

        const tileSize = GAME_CONSTANTS.TILE_SIZE;
        const x = data.spawnX * tileSize + tileSize / 2;
        const y = data.spawnY * tileSize + tileSize / 2;

        if (this.scene.playerSprite) {
            this.scene.playerSprite.setPosition(x, y);
        }
        if (this.scene.vehicleSprite?.active) {
            this.scene.vehicleSprite.setPosition(x, y);
        }
        if (this.scene.player) {
            this.scene.player.x = x;
            this.scene.player.y = y;
            this.scene.player.tileX = data.spawnX;
            this.scene.player.tileY = data.spawnY;
        }

        GameStateStore.patchPlayer({
            x,
            y,
            tileX: data.spawnX,
            tileY: data.spawnY
        });
    }
    
    /**
     * 创建玩家数据对象
     * @param {Object} position - 玩家位置
     * @returns {Object} 玩家数据
     */
    createPlayerData(position) {
        if (!this.scene.gameState || !this.scene.gameState.player) {
            DebugHelper.error('BaseWorldScene: 游戏状态或玩家数据不存在');
            return null;
        }
        
        const playerData = this.scene.gameState.player;
        
        // 多人游戏模式下，如果玩家数据不完整，使用默认值
        const isMultiplayer = this.scene.isMultiplayer;
        
        // 确保 element 有默认值（多人游戏模式下可能未选择角色）
        let element = playerData.element;
        if (!element || element.trim() === '') {
            DebugHelper.warn('BaseWorldInitializer: playerData.element 为空，使用默认值 player1');
            element = 'player1';
            // 更新 gameState 中的值
            if (this.scene.gameState && this.scene.gameState.player) {
                this.scene.gameState.player.element = element;
            }
        }
        
        // 多人游戏模式下，如果玩家名称为空，尝试从多人游戏配置中获取
        let playerName = playerData.name;
        if ((!playerName || playerName.trim() === '') && isMultiplayer) {
            if (this.scene.gameState.multiplayer && this.scene.gameState.multiplayer.playerName) {
                playerName = this.scene.gameState.multiplayer.playerName;
                // 更新 gameState 中的值
                if (this.scene.gameState && this.scene.gameState.player) {
                    this.scene.gameState.player.name = playerName;
                }
            } else {
                playerName = '玩家';
            }
        } else if (!playerName || playerName.trim() === '') {
            playerName = '玩家';
        }
        
        // 多人游戏模式下，如果基础属性为0或未设置，使用合理的默认值
        const defaultHp = isMultiplayer && (playerData.hp === 0 || !playerData.hp) ? 100 : (playerData.hp || 100);
        const defaultMaxHp = isMultiplayer && (playerData.maxHp === 0 || !playerData.maxHp) ? 100 : (playerData.maxHp || 100);
        const defaultAttack = isMultiplayer && (playerData.attack === 0 || !playerData.attack) ? 10 : (playerData.attack || 10);
        const defaultDefense = isMultiplayer && (playerData.defense === 0 || !playerData.defense) ? 10 : (playerData.defense || 10);
        
        const tileSize = GAME_CONSTANTS.TILE_SIZE;

        return {
            x: position.x,
            y: position.y,
            tileX: Math.round((position.x - tileSize / 2) / tileSize),
            tileY: Math.round((position.y - tileSize / 2) / tileSize),
            speed: 60,
            direction: 'down',
            name: playerName,
            element: element,
            level: playerData.level || 1,
            exp: playerData.exp || 0,
            nextLevelExp: playerData.nextLevelExp || 100,
            hp: defaultHp,
            maxHp: defaultMaxHp,
            cost: playerData.cost || 0,
            maxCost: playerData.maxCost || 0,
            attack: defaultAttack,
            defense: defaultDefense,
            skills: playerData.skills || [],
            money: playerData.money || 0,
            battle: playerData.battle || '主角1',
            vehicle: playerData.vehicle || null,
            inVehicle: playerData.inVehicle || false,
            inventory: playerData.inventory || [],
            equipment: playerData.equipment || []
        };
    }
    
    /**
     * 初始化模块
     */
    initializeModules() {
        this.scene.worldMap = new WorldMap(this.scene);
        this.scene.worldNPC = new WorldNPC(this.scene);
        this.scene.worldPlayer = new WorldPlayer(this.scene);
        this.scene.worldUI = new WorldUI(this.scene);
        this.scene.worldInteraction = new WorldInteraction(this.scene);
    }
    
    /**
     * 创建世界地图
     */
    createWorldMap() {
        if (this.scene.isChunkedWorld) {
            return;
        }
        this.scene.worldMap.createMap();
    }
    
    /**
     * 创建NPC
     */
    createNPCs() {
        const npcConfigs = this.scene.worldMapJson.npcs || [];
        if (npcConfigs.length > 0) {
            this.scene.worldNPC.initialize(npcConfigs);
            
            // 将障碍NPC添加到障碍组
            if (this.scene.worldMap.obstacles) {
                this.scene.worldNPC.addToObstacleGroup(this.scene.worldMap.obstacles);
            }
        }
    }
    
    /**
     * 创建地图中的固定敌人
     */
    createEnemies() {
        // 使用boss字段替代enemies字段
        const enemyConfigs = this.scene.worldMapJson.boss || this.scene.worldMapJson.enemies || [];
        if (enemyConfigs.length === 0) return;
        
        const enemiesData = this.scene.cache.json.get('enemies');
        if (!enemiesData) {
            DebugHelper.warn('BaseWorldScene: 无法加载敌人数据');
            return;
        }
        
        // 将敌人配置转换为NPC格式，但标记为敌人
        const enemyNPCs = enemyConfigs.map(enemyConfig => {
            // 从enemies.json中查找对应的敌人数据（在所有区域中查找）
            let baseEnemyData = null;
            for (const area in enemiesData) {
                if (Array.isArray(enemiesData[area])) {
                    baseEnemyData = enemiesData[area].find(e => e.name === enemyConfig.name);
                    if (baseEnemyData) break;
                }
            }
            
            if (!baseEnemyData) {
                DebugHelper.warn(`BaseWorldScene: 未找到敌人数据: ${enemyConfig.name}`);
                return null;
            }
            
            // 合并基础敌人数据和地图配置（地图配置优先）
            // 这样可以在地图JSON中覆盖或添加boss标记、drops等
            const enemyData = {
                ...baseEnemyData,
                ...enemyConfig, // 地图配置中的字段会覆盖基础数据
                // 合并drops数组（如果两者都有）
                drops: enemyConfig.drops || baseEnemyData.drops || [],
                // 保留boss标记（如果地图配置中有）
                boss: enemyConfig.boss !== undefined ? enemyConfig.boss : baseEnemyData.boss
            };
            
            return {
                name: enemyConfig.name,
                texture: enemyConfig.texture || enemyConfig.name,
                tileX: enemyConfig.tileX,
                tileY: enemyConfig.tileY,
                obstacle: enemyConfig.obstacle !== false, // 默认是障碍物
                isEnemy: true, // 标记为敌人
                enemyData: enemyData, // 保存合并后的敌人战斗数据
                bgm: enemyConfig.bgm || null // 保存BGM配置
            };
        }).filter(npc => npc !== null);
        
        if (enemyNPCs.length > 0) {
            // 使用WorldNPC模块创建敌人（作为特殊NPC）
            this.scene.worldNPC.initialize(enemyNPCs);
            
            // 将障碍敌人添加到障碍组
            if (this.scene.worldMap.obstacles) {
                this.scene.worldNPC.addToObstacleGroup(this.scene.worldMap.obstacles);
            }
            
            DebugHelper.log(`BaseWorldScene: 创建了 ${enemyNPCs.length} 个敌人`);
        }
    }
    
    /**
     * 创建玩家和战车
     */
    createPlayer() {
        if (!this.scene.player) return;
        
        // 检查是否是副本场景或城镇场景
        const sceneConfig = this.scene.worldMapJson?.sceneConfig;
        const sceneKey = this.scene.scene.key;
        const cave = isCave(sceneConfig);
        const town = isTown(sceneConfig, sceneKey);
        
        // 在城镇中，如果玩家在车上，强制下车
        if (town) {
            const isInVehicle = this.scene.inVehicle || (this.scene.player && this.scene.player.inVehicle);
            if (isInVehicle) {
                DebugHelper.log('createPlayer: 城镇中强制下车');
                this.scene.inVehicle = false;
                this.scene.player.inVehicle = false;
                GameStateStore.patchPlayer({ inVehicle: false });
            }
        }
        
        // 在副本中，必须要有可以正常行驶的战车才能进入
        if (cave) {
            // 检查是否有战车
            if (!this.scene.player.vehicle) {
                DebugHelper.log('createPlayer: 副本中必须有战车，没有战车，传送到出口');
                this.scene.needsCaveTeleport = true;
                return; // 不创建玩家，因为即将传送
            }
            
            // 检查战车是否可以正常行驶（可以上车）
            const canBoard = VehicleEquipmentSystem.canBoard(this.scene.player.vehicle);
            if (!canBoard.canBoard) {
                DebugHelper.log('createPlayer: 副本中战车不能正常行驶，传送到出口', canBoard.message);
                this.scene.needsCaveTeleport = true;
                return; // 不创建玩家，因为即将传送
            }
            
            // 如果有战车且可以正常行驶，但玩家不在车上，强制上车
            const isInVehicle = this.scene.inVehicle || (this.scene.player && this.scene.player.inVehicle);
            if (!isInVehicle) {
                DebugHelper.log('createPlayer: 副本中强制上车');
                this.scene.inVehicle = true;
                this.scene.player.inVehicle = true;
                GameStateStore.patchPlayer({ inVehicle: true });
            }
        }
        
        // 创建玩家角色
        this.scene.playerSprite = this.scene.worldPlayer.createPlayerCharacter(this.scene.player);
        
        // 创建战车（如果玩家有战车）
        if (this.scene.player.vehicle) {
            this.scene.vehicleSprite = this.scene.worldPlayer.createVehicle(
                this.scene.player.x, 
                this.scene.player.y, 
                this.scene.player.vehicle
            );
            
            // 根据上下车状态设置显示
            if (this.scene.inVehicle) {
                this.scene.playerSprite.setVisible(false);
            } else {
                this.scene.playerSprite.setVisible(true);
            }
        }
        
    }
    
    /**
     * 设置碰撞
     */
    setupCollisions() {
        if (this.scene.playerSprite) {
            this.scene.worldMap.setupCollision(this.scene.playerSprite);
        }
        if (this.scene.vehicleSprite) {
            this.scene.worldMap.setupCollision(this.scene.vehicleSprite);
        }
        
        // 如果是多人模式，设置本地玩家引用
        if (this.scene.isMultiplayer && this.scene.multiplayerPlayerManager) {
            // 延迟设置，确保玩家精灵已创建
            this.scene.time.delayedCall(1000, () => {
                if (this.scene.multiplayerPlayerManager && this.scene.playerSprite) {
                    this.scene.multiplayerPlayerManager.setLocalPlayer(this.scene.playerSprite, this.scene.vehicleSprite);
                }
            });
        }
    }
    
    /**
     * 创建UI
     */
    createUI() {
        this.scene.worldUI.createStatusBar();
    }
    
    /**
     * 设置交互和控制
     */
    setupInteractions() {
        this.scene.worldInteraction.setupControls();
    }
    
    /**
     * 设置相机
     */
    setupCamera() {
        // 如果需要传送（玩家精灵未创建），不设置相机跟随
        if (this.scene.needsCaveTeleport || !this.scene.playerSprite) {
            return;
        }
        
        this.scene.cameras.main.startFollow(this.scene.playerSprite);
        
        // 如果地图比屏幕小，需要限制相机移动范围以保持地图居中
        if (this.scene.worldMap && this.scene.worldMap.worldBounds && this.scene.worldMap.worldBounds.needsCentering) {
            const bounds = this.scene.worldMap.worldBounds;
            const screenWidth = this.scene.cameras.main.width;
            const screenHeight = this.scene.cameras.main.height;
            
            // 计算相机可以移动的范围（相机中心的位置范围）
            const halfScreenWidth = screenWidth / 2;
            const halfScreenHeight = screenHeight / 2;
            
            // 相机中心的最小和最大位置
            const minCenterX = bounds.minX - bounds.offsetX + halfScreenWidth;
            const maxCenterX = bounds.maxX + bounds.offsetX - halfScreenWidth;
            const minCenterY = bounds.minY - bounds.offsetY + halfScreenHeight;
            const maxCenterY = bounds.maxY + bounds.offsetY - halfScreenHeight;
            
            // 存储这些值供更新循环使用
            this.scene.cameraCenterBounds = {
                minX: minCenterX,
                maxX: maxCenterX,
                minY: minCenterY,
                maxY: maxCenterY
            };
        }
    }
    
    /**
     * 播放BGM（按需加载音频资源）
     */
    async playBGM() {
        const volume = SettingsHelper.getBGMPlayVolume();
        let bgmKey;

        if (this.scene.config.bgmKey) {
            bgmKey = this.scene.config.bgmKey;
        } else {
            bgmKey = this.scene.inVehicle ? 'world_bgm2' : 'world_bgm1';
        }

        try {
            await ensureAudios(this.scene, [bgmKey]);
        } catch (error) {
            DebugHelper.warn('BaseWorldInitializer: BGM 加载失败', error);
            return;
        }

        this.scene.bgm = this.scene.sound.add(bgmKey, { loop: true, volume: volume });
        this.scene.bgm.play();
    }
    
    /**
     * 显示欢迎消息（使用轻量级提示，自动消失）
     * 优先使用配置的welcomeMessage，否则使用JSON中的name字段
     */
    showWelcomeMessage() {
        if (this.scene.isChunkedWorld && this.scene.chunkManager) {
            this.scene.time.delayedCall(500, () => {
                this.scene.chunkManager.notifyCurrentRegion();
            });
            return;
        }

        let message = null;
        
        // 优先使用配置的welcomeMessage
        if (this.scene.config.welcomeMessage) {
            if (typeof this.scene.config.welcomeMessage === 'function' && this.scene.player) {
                message = this.scene.config.welcomeMessage(this.scene.player.name);
            } else {
                message = this.scene.config.welcomeMessage;
            }
        }
        // 如果没有配置welcomeMessage，使用JSON中的name字段
        else if (this.scene.worldMapJson && this.scene.worldMapJson.name) {
            message = this.scene.worldMapJson.name;
        }
        
        // 如果有消息，显示提示
        if (message) {
            this.scene.time.delayedCall(500, () => {
                MessageHelper.showSceneNotification(this.scene, message, 1000);
            });
        }
    }
    
    /**
     * 执行场景淡入效果
     */
    performTransitionIn() {
        if (this.scene.needsTransitionIn) {
            // 创建一个黑色覆盖层
            const { width, height, centerX, centerY } = this.scene.cameras.main;
            const fadeOverlay = this.scene.add.rectangle(centerX, centerY, width, height, 0x000000, 1);
            fadeOverlay.setScrollFactor(0);
            fadeOverlay.setDepth(10000);
            
            // 淡出覆盖层（300ms）
            this.scene.tweens.add({
                targets: fadeOverlay,
                alpha: 0,
                duration: 300,
                onComplete: () => {
                    fadeOverlay.destroy();
                    this.scene.movementEnabled = true;
                    this.scene.isTransitioning = false;  // 淡入完成，重置过渡标志
                }
            });
        } else {
            // 没有过渡时，确保移动已启用
            this.scene.movementEnabled = true;
            this.scene.isTransitioning = false;
        }
    }
}

